import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import DOMPurify from 'dompurify';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Plus,
  Globe,
  FileText,
  Eye,
  EyeOff,
  Edit,
  Trash2,
  MoreHorizontal,
  ExternalLink,
  Layout,
  Type,
  Image
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';

const WebsitePage = () => {
  const { API, getAuthHeaders } = useAuth();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [previewPage, setPreviewPage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    is_published: false
  });

  const fetchPages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/pages`, getAuthHeaders());
      setPages(response.data);
    } catch (error) {
      console.error('Errore nel caricamento pagine:', error);
    } finally {
      setLoading(false);
    }
  }, [API, getAuthHeaders]);

  const fetchTemplates = useCallback(async () => {
    // templates loaded separately if needed
  }, []);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedPage) {
        await axios.put(`${API}/pages/${selectedPage.id}`, formData, getAuthHeaders());
      } else {
        await axios.post(`${API}/pages`, formData, getAuthHeaders());
      }
      fetchPages();
      resetForm();
    } catch (error) {
      console.error('Errore nel salvataggio pagina:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa pagina?')) return;
    try {
      await axios.delete(`${API}/pages/${id}`, getAuthHeaders());
      fetchPages();
    } catch (error) {
      console.error('Errore nell\'eliminazione pagina:', error);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', slug: '', content: '', is_published: false });
    setSelectedPage(null);
    setIsDialogOpen(false);
  };

  const openEdit = (page) => {
    setSelectedPage(page);
    setFormData({
      title: page.title,
      slug: page.slug,
      content: page.content,
      is_published: page.is_published
    });
    setIsDialogOpen(true);
  };

  const generateSlug = (title) => {
    return title.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  const stats = {
    total: pages.length,
    published: pages.filter(p => p.is_published).length,
    draft: pages.filter(p => !p.is_published).length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="website-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-outfit">Costruttore Sito Web</h1>
          <p className="text-muted-foreground mt-1">Crea e gestisci le pagine del tuo sito</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setIsDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button data-testid="create-page-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuova Pagina
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="font-outfit">
                {selectedPage ? 'Modifica Pagina' : 'Crea Nuova Pagina'}
              </DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[70vh] pr-4">
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Titolo Pagina *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => {
                        const title = e.target.value;
                        setFormData({
                          ...formData, 
                          title,
                          slug: formData.slug || generateSlug(title)
                        });
                      }}
                      placeholder="Home Page"
                      required
                      data-testid="page-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="slug">URL Slug *</Label>
                    <div className="flex items-center">
                      <span className="text-sm text-muted-foreground mr-1">/</span>
                      <Input
                        id="slug"
                        value={formData.slug}
                        onChange={(e) => setFormData({...formData, slug: generateSlug(e.target.value)})}
                        placeholder="home-page"
                        required
                        data-testid="page-slug-input"
                      />
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="content">Contenuto Pagina (HTML)</Label>
                  <Textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                    placeholder="<h1>Benvenuto nel nostro sito</h1>
<p>Questo è il contenuto della tua pagina...</p>"
                    rows={12}
                    className="font-mono text-sm"
                    data-testid="page-content-input"
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                  <div>
                    <Label htmlFor="published" className="text-base">Pubblica Pagina</Label>
                    <p className="text-sm text-muted-foreground">Rendi questa pagina visibile sul tuo sito</p>
                  </div>
                  <Switch
                    id="published"
                    checked={formData.is_published}
                    onCheckedChange={(checked) => setFormData({...formData, is_published: checked})}
                    data-testid="page-publish-switch"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm}>Annulla</Button>
                  <Button type="submit" data-testid="save-page-btn">
                    {selectedPage ? 'Aggiorna' : 'Crea'} Pagina
                  </Button>
                </div>
              </form>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </div>

      {/* Statistiche */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Layout className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold font-outfit">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Pagine Totali</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Eye className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold font-outfit">{stats.published}</p>
                <p className="text-xs text-muted-foreground">Pubblicate</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-500/10 flex items-center justify-center">
                <EyeOff className="w-5 h-5 text-gray-500" />
              </div>
              <div>
                <p className="text-2xl font-bold font-outfit">{stats.draft}</p>
                <p className="text-xs text-muted-foreground">Bozze</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista Pagine */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5">
          <Card>
            <CardHeader>
              <CardTitle className="font-outfit">Pagine</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {pages.length > 0 ? (
                <div className="divide-y divide-border">
                  {pages.map((page, index) => (
                    <div 
                      key={page.id}
                      className={`p-4 cursor-pointer transition-colors hover:bg-muted/50 ${previewPage?.id === page.id ? 'bg-muted' : ''}`}
                      onClick={() => setPreviewPage(page)}
                      data-testid={`page-item-${page.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 text-muted-foreground" />
                          </div>
                          <div>
                            <h4 className="font-medium">{page.title}</h4>
                            <p className="text-sm text-muted-foreground">/{page.slug}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant={page.is_published ? 'default' : 'secondary'} className="text-xs">
                                {page.is_published ? 'Pubblicata' : 'Bozza'}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                Aggiornata {format(parseISO(page.updated_at), 'd MMM', { locale: it })}
                              </span>
                            </div>
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" onClick={(e) => e.stopPropagation()}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEdit(page); }}>
                              <Edit className="w-4 h-4 mr-2" />
                              Modifica
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setPreviewPage(page); }}>
                              <Eye className="w-4 h-4 mr-2" />
                              Anteprima
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => { e.stopPropagation(); handleDelete(page.id); }}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Elimina
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <Globe className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="font-semibold font-outfit mb-1">Nessuna pagina</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Crea la tua prima pagina per iniziare a costruire il tuo sito
                  </p>
                  <Button onClick={() => setIsDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Crea Pagina
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Pannello Anteprima */}
        <div className="lg:col-span-7">
          <Card className="h-full">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="font-outfit">Anteprima</CardTitle>
                {previewPage && (
                  <div className="flex items-center gap-2">
                    <Badge variant={previewPage.is_published ? 'default' : 'secondary'}>
                      {previewPage.is_published ? 'Pubblicata' : 'Bozza'}
                    </Badge>
                    <Button variant="outline" size="sm" onClick={() => openEdit(previewPage)}>
                      <Edit className="w-3.5 h-3.5 mr-1" />
                      Modifica
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {previewPage ? (
                <div className="p-6">
                  <div className="mb-4 pb-4 border-b">
                    <h2 className="text-xl font-bold font-outfit">{previewPage.title}</h2>
                    <p className="text-sm text-muted-foreground">/{previewPage.slug}</p>
                  </div>
                  <div 
                    className="prose prose-sm dark:prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewPage.content || '<p class="text-muted-foreground">Nessun contenuto</p>') }}
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-center p-8">
                  <Eye className="w-12 h-12 text-muted-foreground/50 mb-4" />
                  <h3 className="font-semibold font-outfit mb-1">Seleziona una pagina per l'anteprima</h3>
                  <p className="text-sm text-muted-foreground">
                    Clicca su una pagina dalla lista per vedere il suo contenuto
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default WebsitePage;
